import { useEffect, useMemo, useState } from "react";
import AxiosInstance from "../Home/AxiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  useResizeColumns,
  usePagination,
} from "react-table";
import { FiEdit, FiTrash, FiArrowLeft } from "react-icons/fi";
import UserHeader from "./UserHeader";
import Footer from "../Home/Footer";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "./CourseMaterial.css";
import "./employerPortal.css";

export default function CourseMaterial() {
  const navigate = useNavigate();
  const { courseMaterialId } = useParams();

  const [batches, setBatches] = useState([]);
  const [topics, setTopics] = useState([]);

  const [batchFilter, setBatchFilter] = useState("");
  const [topicFilter, setTopicFilter] = useState("");

  const [editingBatch, setEditingBatch] = useState(null);
  const [editedBatch, setEditedBatch] = useState({});
  const [batchGlobalFilter, setBatchGlobalFilter] = useState("");
  const [editingTopic, setEditingTopic] = useState(null);
  const [editedTopic, setEditedTopic] = useState({});
  const [topicGlobalFilter, setTopicGlobalFilter] = useState("");

  // Fetch all data
  useEffect(() => {
    // Fetch batches
    AxiosInstance.get(`companies/batches/${courseMaterialId}`)
      .then((response) => {
        setBatches(response.data);
      })
      .catch((error) => {
        console.error("Error fetching batches:", error);
      });

    // Fetch topics
    AxiosInstance.get(`companies/topics/${courseMaterialId}/`)
      .then((response) => {
        setTopics(response.data);
      })
      .catch((error) => {
        console.error("Error fetching topics:", error);
      });
  }, [courseMaterialId]);

  // batch columns and table
  const batchColumns = useMemo(
    () => [
      {
        Header: "Batch",
        accessor: "batch_id",
        resizable: true,
        Cell: ({ value }) => (
          <button
            className="btn btn-link p-0"
            onClick={() => navigate(`/enrollment/${value}`)}
            style={{
              textDecoration: "underline",
              color: "#0d6efd",
              cursor: "pointer",
            }}
          >
            {value}
          </button>
        ),
      },
      { Header: "batch Name", accessor: "batch_name", resizable: true },
      { Header: "Duration", accessor: "duration", resizable: true },
      { Header: "Time Zone", accessor: "timezone", resizable: true },
      { Header: "Timings", accessor: "timings", resizable: true },
      {
        Header: "Batch starts at",
        accessor: "batch_starts_at",
        resizable: true,
      },
      { Header: "Mode", accessor: "mode", resizable: true },
      { Header: "Batch Limit", accessor: "max_students", resizable: true },
      { Header: "Address", accessor: "venue_address", resizable: true },
      { Header: "Is Published?", accessor: "is_published", resizable: true },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                const original = row.original;
                setEditingBatch(original);
                setEditedBatch(original);
              }}
            >
              <FiEdit />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeleteBatch(row.original.batch_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps: getBatchTableProps,
    getTableBodyProps: getBatchTableBodyProps,
    headerGroups: batchHeaderGroups,
    prepareRow: prepareBatchRow,
    page: batchPage,
    canPreviousPage: canPreviousBatchPage,
    canNextPage: canNextBatchPage,
    pageOptions: batchPageOptions,
    nextPage: nextBatchPage,
    previousPage: previousBatchPage,
    state: { pageIndex: batchPageIndex },
    setGlobalFilter: setBatchGlobalFilterFunc,
  } = useTable(
    {
      columns: batchColumns,
      data: batches,
      initialState: { pageSize: 8 },
    },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  function formatContent(text) {
    if (!text) return "";

    // Convert lines starting with "-" or "*" into <li> items
    const lines = text.split("\n");

    const listItems = lines.map((line) => {
      const trimmed = line.trim();
      if (trimmed.startsWith("-") || trimmed.startsWith("*")) {
        return `<li>${trimmed.substring(1).trim()}</li>`;
      } else {
        return `<p>${trimmed}</p>`;
      }
    });

    // Wrap <li> items inside <ul> if any exist
    const ulItems = listItems.filter((item) => item.startsWith("<li>"));
    const otherItems = listItems.filter((item) => !item.startsWith("<li>"));

    const listHtml = ulItems.length > 0 ? `<ul>${ulItems.join("")}</ul>` : "";
    return `${listHtml}${otherItems.join("")}`;
  }

  // Topic columns and table
  const topicColumns = useMemo(
    () => [
      {
        Header: "Topic Name",
        accessor: "topic_name",
        resizable: true,
        Cell: ({ row }) => (
          <span
            className="text-primary text-decoration-underline"
            style={{ cursor: "pointer" }}
            onClick={() => {
              localStorage.setItem("topic_id", row.original.topic_id);
              navigate(`/sub-topics/${row.original.topic_id}`);
            }}
          >
            {row.values.topic_name}
          </span>
        ),
      },
      {
        Header: "Content",
        accessor: "content",
        resizable: true,
        Cell: ({ value }) => (
          <div
            dangerouslySetInnerHTML={{ __html: formatContent(value) }}
            style={{ whiteSpace: "normal", lineHeight: "1.5" }}
          />
        ),
      },
      { Header: "Video Link", accessor: "video_link", resizable: true },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                const original = row.original;
                setEditingTopic(original);
                setEditedTopic(original);
              }}
            >
              <FiEdit />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeleteTopic(row.original.topic_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps: getTopicTableProps,
    getTableBodyProps: getTopicTableBodyProps,
    headerGroups: topicHeaderGroups,
    prepareRow: prepareTopicRow,
    page: topicPage,
    canPreviousPage: canPreviousTopicPage,
    canNextPage: canNextTopicPage,
    pageOptions: topicPageOptions,
    nextPage: nextTopicPage,
    previousPage: previousTopicPage,
    state: { pageIndex: topicPageIndex },
    setGlobalFilter: setTopicGlobalFilterFunc,
  } = useTable(
    {
      columns: topicColumns,
      data: topics,
      initialState: { pageSize: 8 },
    },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  useEffect(() => {
    setBatchGlobalFilterFunc(batchGlobalFilter);
  }, [batchGlobalFilter, setBatchGlobalFilterFunc]);

  useEffect(() => {
    setTopicGlobalFilterFunc(topicGlobalFilter);
  }, [topicGlobalFilter, setTopicGlobalFilterFunc]);

  // Batch handlers
  const handleSaveBatch = () => {
    const formData = new FormData();

    // Append only defined fields
    formData.append("duration", editedBatch.duration || "");
    formData.append("timezone", editedBatch.timezone || "");
    formData.append("timings", editedBatch.timings || "");
    formData.append("batch_starts_at", editedBatch.batch_starts_at || "");
    formData.append("max_students", editedBatch.max_students || "");
    formData.append("mode", editedBatch.mode || "");
    if (editedBatch.mode === "Offline") {
      formData.append("venue_address", editedBatch.venue_address || "");
    } else {
      formData.append("venue_address", "");
    }
    formData.append("is_published", editedBatch.is_published || "");

    AxiosInstance.put(`companies/batch/${editedBatch.batch_id}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then(() => {
        setEditingBatch(null);
        return AxiosInstance.get(`companies/batches/${courseMaterialId}`);
      })
      .then((response) => setBatches(response.data))
      .catch((error) => console.error("Error updating batch:", error));
  };

  const handleDeleteBatch = async (batchId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this batch?"
    );
    if (!confirmDelete) return;
    try {
      const response = await AxiosInstance.delete(
        `companies/course/batches/${batchId}/`
      );

      // Update frontend state if you have a list of batches
      setBatches((prevBatches) =>
        prevBatches.filter((batch) => batch.batch_id !== batchId)
      );
    } catch (error) {
      console.error(
        "Error deleting batch:",
        error.response?.data || error.message
      );
    }
  };

  // Topic handlers
  const handleSaveTopic = () => {
    const formData = new FormData();
    formData.append("topic_name", editedTopic.topic_name);
    formData.append("content", editedTopic.content);

    // Append video file only if it exists
    if (editedTopic.video_link instanceof File) {
      formData.append("video_link", editedTopic.video_link);
    }

    AxiosInstance.put(`companies/topic/${editedTopic.topic_id}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then(() => {
        setEditingTopic(null);
        return AxiosInstance.get(`companies/topics/${courseMaterialId}/`);
      })
      .then((response) => setTopics(response.data))
      .catch((error) => console.error("Error updating topic:", error));
  };

  const handleDeleteTopic = (topicId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this topic?"
    );
    if (!confirmDelete) return;

    AxiosInstance.delete(`companies/course/topics/${topicId}/`)
      .then(() => AxiosInstance.get(`companies/topics/${courseMaterialId}/`))
      .then((response) => setTopics(response.data))
      .catch((error) => console.error("Error deleting topic:", error));
  };

  return (
    <div className="CompanyPage als-employer-page">
      <UserHeader />
      <div className="company-container py-4">
        <div className="mb-3">
          <button
            className="navigate-button btn btn-outline-primary d-flex align-items-center"
            onClick={() => navigate(-1)}
            style={{ gap: "6px" }}
          >
            <FiArrowLeft /> Back
          </button>
        </div>
        {/* Batch Section */}
        <h2 className="section-heading">Batches:</h2>
        <div className="card-custom">
          <div className="d-flex justify-content-between mb-3">
            <input
              type="text"
              className="form-control w-50"
              value={batchFilter}
              onChange={(e) => {
                setBatchFilter(e.target.value);
                setBatchGlobalFilter(e.target.value);
              }}
              placeholder="Search batch..."
            />
            <button
              className="add-button"
              onClick={() => navigate(`/user-batch`)}
            >
              Add
            </button>
          </div>

          <div className="table-responsive">
            <table
              {...getBatchTableProps()}
              className="table table-bordered table-striped"
            >
              <thead className="table-light">
                {batchHeaderGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={headerGroup.id}
                  >
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        key={column.id}
                      >
                        <div {...column.getHeaderProps()}>
                          {column.render("Header")}
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " ▼"
                              : " ▲"
                            : ""}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getBatchTableBodyProps()}>
                {batchPage.length === 0 ? (
                  <tr>
                    <td colSpan={batchColumns.length}>No batches found.</td>
                  </tr>
                ) : (
                  batchPage.map((row) => {
                    prepareBatchRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} key={cell.column.id}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

            <div className="pagination d-flex justify-content-center align-items-center gap-3 mt-3">
              <button
                onClick={() => previousBatchPage()}
                disabled={!canPreviousBatchPage}
                className="btn btn-outline-primary"
              >
                Previous
              </button>
              <span>
                Page{" "}
                <strong>
                  {batchPageIndex + 1} of {batchPageOptions.length}
                </strong>
              </span>
              <button
                onClick={() => nextBatchPage()}
                disabled={!canNextBatchPage}
                className="btn btn-outline-primary"
              >
                Next
              </button>
            </div>
          {editingBatch && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Batch</h5>
                  </div>

                  <div className="modal-body">
                    <form>
                      <div className="row mb-3">
                        <div className="col">
                          <label className="form-label">Batch Name</label>
                          <select
                            className="form-select"
                            name="batch_name"
                            value={editedBatch.batch_name || ""}
                            onChange={(e) =>
                              setEditedBatch({
                                ...editedBatch,
                                batch_name: e.target.value,
                              })
                            }
                          >
                            <option value="">Select Batch Name</option>
                            <option value="Morning Batch">Morning Batch</option>
                            <option value="Afternoon Batch">
                              Afternoon Batch
                            </option>
                            <option value="Evening Batch">Evening Batch</option>
                            <option value="Weekdays Batch">
                              Weekdays Batch
                            </option>
                            <option value="Weekend Batch">Weekend Batch</option>
                            <option value="Fast Track Batch">
                              Fast Track Batch
                            </option>
                          </select>
                        </div>

                        <div className="col">
                          <label className="form-label">Duration</label>
                          <input
                            type="text"
                            className="form-control"
                            name="duration"
                            value={editedBatch.duration || ""}
                            onChange={(e) =>
                              setEditedBatch({
                                ...editedBatch,
                                duration: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col">
                          <label className="form-label">Time Zone</label>
                          <select
                            className="form-select"
                            name="timezone"
                            value={editedBatch.timezone || ""}
                            onChange={(e) =>
                              setEditedBatch({
                                ...editedBatch,
                                timezone: e.target.value,
                              })
                            }
                          >
                            <option value="">Select Timezone</option>
                            <option value="IST">IST</option>
                            <option value="EST">EST</option>
                            <option value="PST">PST</option>
                            <option value="GMT">GMT</option>
                            <option value="CET">CET</option>
                          </select>
                        </div>

                        <div className="col">
                          <label className="form-label">Timings</label>
                          <input
                            type="text"
                            className="form-control"
                            name="timings"
                            value={editedBatch.timings || ""}
                            onChange={(e) =>
                              setEditedBatch({
                                ...editedBatch,
                                timings: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col">
                          <label className="form-label">Batch Starts At</label>
                          <input
                            type="date"
                            className="form-control"
                            name="batch_starts_at"
                            value={editedBatch.batch_starts_at || ""}
                            onChange={(e) =>
                              setEditedBatch({
                                ...editedBatch,
                                batch_starts_at: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="col">
                          <label className="form-label">Mode</label>
                          <select
                            className="form-select"
                            name="mode"
                            value={editedBatch.mode || ""}
                            onChange={(e) =>
                              setEditedBatch({
                                ...editedBatch,
                                mode: e.target.value,
                              })
                            }
                          >
                            <option value="">Select Mode</option>
                            <option value="Online">Online</option>
                            <option value="Offline">Offline</option>
                          </select>
                        </div>
                      </div>

                      {editedBatch.mode === "Offline" && (
                        <>
                          <div className="mb-3">
                            <label className="form-label">Batch Limit</label>
                            <input
                              type="number"
                              className="form-control"
                              name="max_students"
                              value={editedBatch.max_students || ""}
                              onChange={(e) =>
                                setEditedBatch({
                                  ...editedBatch,
                                  max_students: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Address</label>
                            <textarea
                              type="text"
                              className="form-control"
                              name="venue_address"
                              value={editedBatch.venue_address || ""}
                              onChange={(e) =>
                                setEditedBatch({
                                  ...editedBatch,
                                  venue_address: e.target.value,
                                })
                              }
                            ></textarea>
                          </div>
                        </>
                      )}

                      <div className="mb-3">
                        <label className="form-label">Is Published?</label>
                        <select
                          className="form-select"
                          name="is_published"
                          value={editedBatch.is_published || ""}
                          onChange={(e) =>
                            setEditedBatch({
                              ...editedBatch,
                              is_published: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Publishing Status</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                    </form>
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setEditingBatch(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveBatch}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Topics Section */}
        <h2 className="section-heading">Topics:</h2>
        <div className="card-custom">
          <div className="d-flex justify-content-between mb-3">
            <input
              type="text"
              className="form-control w-50"
              value={topicFilter}
              onChange={(e) => {
                setTopicFilter(e.target.value);
                setTopicGlobalFilter(e.target.value);
              }}
              placeholder="Search topic..."
            />
            <button
              className="add-button"
              onClick={() => navigate(`/user-topic`)}
            >
              Add
            </button>
          </div>

          <div className="table-responsive">
            <table
              {...getTopicTableProps()}
              className="table table-bordered table-striped"
            >
              <thead className="table-light">
                {topicHeaderGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={headerGroup.id}
                  >
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        key={column.id}
                      >
                        <div {...column.getHeaderProps()}>
                          {column.render("Header")}
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " ▼"
                              : " ▲"
                            : ""}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTopicTableBodyProps()}>
                {topicPage.length === 0 ? (
                  <tr>
                    <td colSpan={topicColumns.length}>No topics found.</td>
                  </tr>
                ) : (
                  topicPage.map((row) => {
                    prepareTopicRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} key={cell.column.id}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

            <div className="pagination d-flex justify-content-center align-items-center gap-3 mt-3">
              <button
                onClick={() => previousTopicPage()}
                disabled={!canPreviousTopicPage}
                className="btn btn-outline-primary"
              >
                Previous
              </button>
              <span>
                Page{" "}
                <strong>
                  {topicPageIndex + 1} of {topicPageOptions.length}
                </strong>{" "}
              </span>
              <button
                onClick={() => nextTopicPage()}
                disabled={!canNextTopicPage}
                className="btn btn-outline-primary"
              >
                Next
              </button>
            </div>
          {editingTopic && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Topic</h5>
                  </div>

                  <div className="modal-body">
                    <form>
                      <div className="mb-3">
                        <label className="form-label">Topic Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="topic_name"
                          value={editedTopic.topic_name || ""}
                          onChange={(e) =>
                            setEditedTopic({
                              ...editedTopic,
                              topic_name: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Content</label>
                        <textarea
                          className="form-control"
                          name="content"
                          rows="6"
                          placeholder="Enter topic content here. Use bullets (- or *) or new lines for paragraphs."
                          value={editedTopic.content || ""}
                          onChange={(e) =>
                            setEditedTopic({
                              ...editedTopic,
                              content: e.target.value,
                            })
                          }
                        ></textarea>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Video File</label>
                        <input
                          type="file"
                          className="form-control"
                          name="video_link"
                          accept="video/*"
                          onChange={(e) =>
                            setEditedTopic({
                              ...editedTopic,
                              video_link: e.target.files[0],
                            })
                          }
                        />
                        {editedTopic.video_link &&
                          typeof editedTopic.video_link === "string" && (
                            <div className="mt-2">
                              <p className="mb-0">
                                Current video:{" "}
                                <strong>
                                  {editedTopic.video_link.split("/").pop()}
                                </strong>
                              </p>
                            </div>
                          )}
                      </div>
                    </form>
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setEditingTopic(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveTopic}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
